import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { getDeviceId } from '@/lib/deviceId';
import { clearJoin, isJoinValid, parseJoinParam, readJoin, stripJoinParam, writeJoin } from '@/lib/screenJoin';

export type ScreenJoinStatus = 'n/a' | 'checking' | 'joined' | 'required';
export type ScreenJoinReason = 'expired' | 'unknown_tag' | 'disabled' | 'error' | null;

interface JoinableTag {
  id: string;
  tag_public_id: string;
  kind?: 'seat' | 'screen';
}

/**
 * Whether this device may write on a screen tag. On `?j=<code>` the code from
 * the stadium screen is claimed once (claim_screen_join) and the join is kept
 * for 12 h; without a code the stored join decides. Seat tags are 'n/a'.
 * `expire()` is for the guard trigger's `screen_join_required`: the join ran
 * out mid-match (switching the tag off also ends it; rotating the screen
 * token only changes the codes) and the fan must scan again.
 */
export function useScreenJoin(tag: JoinableTag | null | undefined) {
  const isScreen = tag?.kind === 'screen';
  const tagId = tag?.id;
  const publicId = tag?.tag_public_id;
  const [resolved, setResolved] = useState<{ tagId: string; status: ScreenJoinStatus; reason: ScreenJoinReason } | null>(null);

  useEffect(() => {
    if (!isScreen || !tagId || !publicId) return;
    let cancelled = false;
    const settle = (status: ScreenJoinStatus, reason: ScreenJoinReason = null) => {
      if (!cancelled) setResolved({ tagId, status, reason });
    };
    const code = parseJoinParam(window.location.search);
    if (!code) {
      settle(isJoinValid(readJoin(tagId)) ? 'joined' : 'required');
      return () => { cancelled = true; };
    }
    // Clean the URL first, as ?src= is cleaned: a code is not for bookmarks or sharing.
    window.history.replaceState({}, '', stripJoinParam(window.location.pathname + window.location.search));
    // claim_screen_join is newer than the generated types, hence the untyped client.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (supabase as any)
      .rpc('claim_screen_join', { _tag_public_id: publicId, _code: code, _device_id: getDeviceId() })
      .then(({ data, error }: { data: { ok?: boolean; valid_until?: string; reason?: ScreenJoinReason } | null; error: unknown }) => {
        if (!error && data?.ok && data.valid_until) {
          writeJoin(tagId, data.valid_until);
          settle('joined');
          return;
        }
        // A stale code must not erase a join this device already holds.
        if (isJoinValid(readJoin(tagId))) { settle('joined'); return; }
        settle('required', error ? 'error' : (data?.reason ?? 'expired'));
      });
    return () => { cancelled = true; };
  }, [isScreen, tagId, publicId]);

  const expire = useCallback(() => {
    if (!tagId) return;
    clearJoin(tagId);
    setResolved({ tagId, status: 'required', reason: 'expired' });
  }, [tagId]);

  if (!isScreen || !tagId) return { status: 'n/a' as const, reason: null, expire };
  if (!resolved || resolved.tagId !== tagId) return { status: 'checking' as const, reason: null, expire };
  return { status: resolved.status, reason: resolved.reason, expire };
}
